import { normalizeExerciseName } from '@gymcloud/mixedscript'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { useAuth } from '~/auth/auth-context'
import { useReturnScroll } from '~/lib/opening-set'
import { AmbientGlow, AmbientScope } from '~/components/ambient-glow'
import { EmptyState } from '~/components/empty-state'
import { GetTheApps } from '~/components/get-the-apps'
import { HeroFace, HERO_FACE_SIZES } from '~/components/hero-face'
import { CalendarIcon, LayersIcon, TrophyIcon } from '~/components/icons'
import { KindGlyph } from '~/components/kind-glyph'
import { KindMark } from '~/components/kind-mark'
import { SafeImage } from '~/components/safe-image'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { SetDeck } from '~/components/set-deck'
import { rememberDeck } from '~/lib/set-deck'
import { SetGrid } from '~/components/set-grid'
import { TilePicture } from '~/components/tile-picture'
import { TrendChart, TrendChartSkeleton } from '~/components/trend-chart'
import { SidewaysRow } from '~/components/ui/sideways-row'
import { WorkoutThumbnail } from '~/components/workout-thumbnail'
import { useWeighIns } from '~/data/weighins-store'
import { useApi, useDataService, useDataState } from '~/data/data-context'
import { workoutsOfExercise } from '~/data/service'
import type { DistanceUnits, LoggedSet, Units } from '~/data/types'
import { TILE_FRAME, TILE_MARK, TILE_SCRIM, tileInk, tileInkQuiet } from '~/lib/tiles'
import { dayKey, formatDayLabel } from '~/lib/dates'
import {
  dailyBest,
  estimatedOneRepMax,
  exerciseMethods,
  exercisePRs,
  exerciseTotals,
  type ExerciseKind,
  type ExercisePR,
  type ExerciseTotals,
} from '~/lib/exercise-insights'
import { weightWeek } from '~/lib/metric-series'
import {
  bodyweightRatioText,
  formatDistance,
  formatDuration,
  formatPace,
  formatSetNumbers,
  formatWeight,
} from '~/lib/units'
import { markPageTransitionSource, pageTransitionName } from '~/lib/view-transitions'

/**
 * The chosen tab, order and grouping ride in the URL, so opening a set and coming back keeps
 * all three.
 */
interface ExerciseSearch {
  readonly method?: string
  readonly order?: string
  readonly byWorkout?: boolean
}

export const Route = createFileRoute('/exercises/$exerciseName')({
  validateSearch: (search): ExerciseSearch => ({
    ...(typeof search.method === 'string' ? { method: search.method } : {}),
    ...(typeof search.order === 'string' && search.order !== 'recent'
      ? { order: search.order }
      : {}),
    ...(search.byWorkout === true ? { byWorkout: true } : {}),
  }),
  component: ExercisePage,
})

const KIND_LABELS: Record<ExerciseKind, string> = {
  lift: 'Lift',
  bodyweight: 'Bodyweight',
  hold: 'Hold',
  run: 'Run',
}

const PR_LABELS: Record<ExercisePR['id'], string> = {
  heaviest: 'Heaviest weight',
  'one-rep-max': 'Best est. 1RM',
  'bodyweight-ratio': 'Best against bodyweight',
  'most-reps': 'Most reps in a set',
  'longest-hold': 'Longest hold',
  'longest-run': 'Longest run',
  'fastest-pace': 'Fastest pace',
}

function formatPRValue(pr: ExercisePR, units: Units, distance: DistanceUnits): string {
  switch (pr.id) {
    case 'heaviest':
    case 'one-rep-max':
      return formatWeight(pr.value, units)
    // Worked out already, so it is only said here — and never null, since a ratio too small
    // to be worth saying is never a record in the first place.
    case 'bodyweight-ratio':
      return bodyweightRatioText(pr.value) ?? ''
    case 'most-reps':
      return `${String(pr.value)} reps`
    case 'longest-hold':
      return formatDuration(pr.value)
    case 'longest-run':
      return formatDistance(pr.value, distance)
    case 'fastest-pace':
      return formatPace(pr.value, distance)
  }
}

/** What the graph's values read as, per kind — the day's best in that kind's own measure. */
function trendFormatter(
  kind: ExerciseKind,
  units: Units,
  distance: DistanceUnits,
): (value: number) => string {
  switch (kind) {
    case 'lift':
      return (value) => formatWeight(value, units)
    case 'bodyweight':
      return (value) => `${String(Math.round(value))} reps`
    case 'hold':
      return formatDuration
    case 'run':
      return (value) => formatDistance(value, distance)
  }
}

/** One headline number, and the glyph that says at a glance which number it is. */
interface HeroStat {
  readonly icon: ReactNode
  readonly label: string
}

/** Every stat's glyph is the same size and the same quiet, whichever glyph it is. */
const STAT_GLYPH = 'h-5 w-5 shrink-0 text-ink-muted'

/** The headline numbers under the name: counts, the kind's total, the kind's best. */
function heroStats(
  kind: ExerciseKind,
  totals: ExerciseTotals,
  prs: readonly ExercisePR[],
  units: Units,
  distance: DistanceUnits,
): HeroStat[] {
  const total = (() => {
    switch (kind) {
      case 'lift':
        return `${formatWeight(totals.weightMovedKg, units)} moved`
      case 'bodyweight':
        return `${String(totals.reps)} reps total`
      case 'hold':
        return `${formatDuration(totals.durationSeconds)} held`
      case 'run':
        return `${formatDistance(totals.distanceMeters, distance)} covered`
    }
  })()
  const best = prs[0]
  return [
    {
      icon: <LayersIcon className={STAT_GLYPH} />,
      // Working sets, and it says so: warm-ups never reach this page, and a count that read as
      // every set would be a number nobody could reconcile with their own logbook.
      label: `${String(totals.setCount)} working ${totals.setCount === 1 ? 'set' : 'sets'}`,
    },
    {
      icon: <CalendarIcon className={STAT_GLYPH} />,
      label: `${String(totals.dayCount)} ${totals.dayCount === 1 ? 'day' : 'days'}`,
    },
    { icon: <KindGlyph kind={kind} className={STAT_GLYPH} />, label: total },
    ...(kind === 'lift' && best
      ? [
          {
            icon: <TrophyIcon className={STAT_GLYPH} />,
            label: `${formatWeight(best.value, units)} heaviest`,
          },
        ]
      : []),
  ]
}

const SETS_SHOWN = 30

/**
 * How big the hero runs. The whole of it is drawn off the shared face size: `--spill` is how far
 * the face hangs below the curve, which is what the page under it is pulled up by and what the
 * curve's own box is measured in.
 */
const HERO_SIZES = [HERO_FACE_SIZES, '[--spill:calc(var(--face)/2_+_4px)]'].join(' ')

/**
 * How much of the curve's box the curve itself is plotted in. The rest is room for the wash to
 * keep falling through, down behind the face standing on the box's edge — so the two are one
 * thing rather than a graph with a circle after it. The box is three spills tall and the band
 * is two of them, so the share holds at whatever size the hero is drawn.
 */
const TREND_CURVE_BAND = 2 / 3

/** A record wears the set that set it, so the row reads as pictures before it reads as numbers. */
const RECORD_CARD = { width: 190, height: 210 }

/**
 * The foot of the sets shown so far. It asks for the next thirty as it comes into view, and
 * says it is working meanwhile so the wait is never an empty page ending without warning.
 */
function MoreSets({ onReach }: { onReach: () => void }) {
  const foot = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const mark = foot.current
    if (mark === null) {
      return
    }
    const watch = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        onReach()
      }
    })
    watch.observe(mark)
    return () => {
      watch.disconnect()
    }
  }, [onReach])
  return (
    <div ref={foot} className="py-6 text-center text-sm text-ink-muted">
      Loading more sets…
    </div>
  )
}

/**
 * The span the exercise covers, said the shortest way that stays true on a single day — an
 * exercise first done and last done on the same day names that day once rather than twice.
 */
function spanOfDays(firstPerformedAt: number | null, lastPerformedAt: number): string {
  const last = formatDayLabel(lastPerformedAt)
  if (firstPerformedAt === null) {
    return `Last done ${last}`
  }
  const first = formatDayLabel(firstPerformedAt)
  return first === last ? `First done ${first}` : `Since ${first} · last done ${last}`
}

interface SetOrderOption {
  readonly id: string
  readonly label: string
  readonly sort: (a: LoggedSet, b: LoggedSet) => number
}

const byRecent = (a: LoggedSet, b: LoggedSet) =>
  b.performedAt - a.performedAt || (a.id < b.id ? 1 : -1)

type Measure = (set: LoggedSet) => number | null

const descending = (measure: Measure) => (a: LoggedSet, b: LoggedSet) =>
  (measure(b) ?? -Infinity) - (measure(a) ?? -Infinity) || byRecent(a, b)

const ascending = (measure: Measure) => (a: LoggedSet, b: LoggedSet) =>
  (measure(a) ?? Infinity) - (measure(b) ?? Infinity) || byRecent(a, b)

const setPace: Measure = (set) =>
  set.durationSeconds === null || set.distanceMeters === null || set.distanceMeters === 0
    ? null
    : set.durationSeconds / (set.distanceMeters / 1000)

/** How the sets can be ordered: most recent always, then the kind's own measures of best. */
function orderOptions(kind: ExerciseKind): SetOrderOption[] {
  const recent: SetOrderOption = { id: 'recent', label: 'Recent', sort: byRecent }
  switch (kind) {
    case 'lift':
      return [
        recent,
        {
          id: 'e1rm',
          label: 'Est. 1RM',
          sort: descending((set) =>
            set.weightKg === null ? null : estimatedOneRepMax(set.weightKg, set.reps),
          ),
        },
        { id: 'heaviest', label: 'Heaviest', sort: descending((set) => set.weightKg) },
      ]
    case 'bodyweight':
      return [recent, { id: 'reps', label: 'Most reps', sort: descending((set) => set.reps) }]
    case 'hold':
      return [
        recent,
        { id: 'longest', label: 'Longest', sort: descending((set) => set.durationSeconds) },
      ]
    case 'run':
      return [
        recent,
        { id: 'farthest', label: 'Farthest', sort: descending((set) => set.distanceMeters) },
        { id: 'fastest', label: 'Fastest', sort: ascending(setPace) },
      ]
  }
}

interface WorkoutBlock {
  readonly key: string
  readonly heading: string
  readonly sets: LoggedSet[]
}

/** The sets dealt into blocks by workout, in order of each workout's freshest set. */
function workoutBlocks(
  ordered: readonly LoggedSet[],
  nameOf: (workoutId: string) => string | null,
): WorkoutBlock[] {
  const blocks: WorkoutBlock[] = []
  const at = new Map<string, number>()
  for (const set of ordered) {
    const key = set.workoutId ?? 'loose'
    const found = at.get(key)
    if (found === undefined) {
      at.set(key, blocks.length)
      blocks.push({
        key,
        heading: set.workoutId === null ? 'No workout' : (nameOf(set.workoutId) ?? 'Workout'),
        sets: [set],
      })
    } else {
      blocks[found]?.sets.push(set)
    }
  }
  return blocks
}

function ExercisePage() {
  const { exerciseName } = Route.useParams()
  // A set opened from this page flies back to a page standing where it was left.
  useReturnScroll()
  const state = useDataState()
  const service = useDataService()
  const api = useApi()
  const { units, distanceUnits } = useAuth()
  // The weigh-ins, which the record against bodyweight is reckoned against. Null until they
  // land; nothing is said about a ratio before then.
  const weighIns = useWeighIns()
  /*
   * What the body weighed around each set, which is the one thing a record against bodyweight
   * needs and the sets themselves do not carry. The trend weight, which is what a set's own
   * ratio is read off, so a set's ratio and the record's agree.
   */
  const bodyweightOn = useCallback(
    (set: LoggedSet) => {
      if (weighIns === null) {
        return null
      }
      const week = weightWeek(weighIns, dayKey(set.performedAt))
      return week.trendNow
    },
    [weighIns],
  )

  const { method, order, byWorkout } = Route.useSearch()
  const navigate = useNavigate()
  const orderId = order ?? 'recent'
  const grouped = byWorkout === true

  const applySets = (nextOrder: string, nextGrouped: boolean) => {
    void navigate({
      to: '/exercises/$exerciseName',
      params: { exerciseName },
      search: {
        ...(method !== undefined ? { method } : {}),
        ...(nextOrder !== 'recent' ? { order: nextOrder } : {}),
        ...(nextGrouped ? { byWorkout: true } : {}),
      },
      replace: true,
      // Reordering the sets is not a journey — the page stays where it is.
      resetScroll: false,
    })
  }

  /*
   * The PRs and graph are drawn from the whole history, so pull it in — page by page. The
   * graph waits for the last of those pages before it is drawn at all: a curve is a shape,
   * and one that redraws into a different shape as each page lands looks like the exercise
   * itself changing rather than the page still arriving.
   *
   * What is held is which exercise the history in hand belongs to, rather than a flag saying it
   * arrived — so moving to another exercise is already back to waiting on the render that
   * moves, with no frame in between where the old curve stands under the new name.
   */
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    void service.loadExerciseHistory(exerciseName).then((complete) => {
      if (live && complete) {
        setLoadedFor(exerciseName)
      }
    })
    return () => {
      live = false
    }
  }, [service, exerciseName])
  const historyLoaded = loadedFor === exerciseName

  /*
   * Keyed by the exercise, the way the history above is. Held as a bare list it survived a move
   * to another exercise, and the rail showed the last one's workouts until this one's arrived —
   * which for an exercise reached in a microtask is long enough to read.
   */
  /*
   * Derived off the mirror as it stands rather than asked once at mount: the mirror fills as
   * the feed lands, and an answer taken before it had would leave this section silently absent
   * on the page it belongs to.
   */
  const theirWorkouts = useMemo(
    () => workoutsOfExercise(exerciseName, state.workouts, state.sets),
    [exerciseName, state.workouts, state.sets],
  )

  const key = normalizeExerciseName(exerciseName)
  // Warm-ups appear nowhere on the page — it reads as the exercise's real work.
  const everySet = useMemo(
    () =>
      state.sets.filter((set) => normalizeExerciseName(set.exerciseName) === key && !set.isWarmup),
    [state.sets, key],
  )

  /*
   * One exercise can be logged more than one way — pull ups for reps and pull ups for
   * duration — and no graph or PR holds both. Each way gets a tab of its own, the most
   * recently logged first, and everything below the tabs is of that one alone.
   */
  const methods = useMemo(() => exerciseMethods(everySet), [everySet])
  const chosen = methods.find((candidate) => candidate.kind === method) ?? methods[0]
  const sets = chosen?.sets ?? []

  useEffect(() => {
    markPageTransitionSource('exercise', chosen?.latestName ?? exerciseName)
  }, [chosen?.latestName, exerciseName])
  const heroRef = useRef<HTMLDivElement>(null)

  if (!historyLoaded) {
    const recent = state.recentExercises.find(
      (candidate) => normalizeExerciseName(candidate.name) === key,
    )
    return (
      <ExerciseHistoryLoading
        name={chosen?.latestName ?? recent?.name ?? exerciseName}
        heroSetId={
          chosen?.sets.find((set) => set.hasThumbnail)?.id ?? recent?.thumbnailSetId ?? null
        }
        api={api}
      />
    )
  }

  if (!chosen) {
    // Not gone — just never logged. A template can name an exercise nobody has done, and any
    // name at all reaches this page now that a name is all an exercise is.
    return (
      <EmptyState
        icon={<LayersIcon className="size-8" />}
        title={`No history for ${exerciseName}`}
        detail="You haven't logged a working set of this exercise yet. Once you do, its progress will live here."
        actions={
          <>
            {/* Logging lives in the apps; the first set of this exercise is theirs to take. */}
            <GetTheApps compact />
            <Link
              to="/"
              className="rounded-full px-5 py-2.5 font-medium text-accent hover:bg-hover"
            >
              Back to the timeline
            </Link>
          </>
        }
      />
    )
  }

  const kind = chosen.kind
  const records = chosen.records
  const totals = exerciseTotals(sets)
  // Held back until the weigh-ins have landed: a rail drawn without them would be saying
  // there is no ratio to show, and it does not know that yet.
  const prs = exercisePRs(sets, kind, weighIns === null ? undefined : bodyweightOn)
  /*
   * A lift whose weigh-ins have not landed. Its ratio record is neither there nor not there
   * yet, and a rail drawn without it would be answering a question it cannot answer — so the
   * card's place is held instead. Only where there are sets to have set a record at all.
   */
  const ratioWaiting = kind === 'lift' && weighIns === null && sets.some((set) => !set.isWarmup)
  const trend = dailyBest(sets, kind)
  const showsThumbnail = (set: LoggedSet) => set.hasThumbnail
  // The exercise thumbnail: the latest set with one, or the server's pick for the row.
  const heroSetId =
    sets.find(showsThumbnail)?.id ??
    state.recentExercises.find((candidate) => normalizeExerciseName(candidate.name) === key)
      ?.thumbnailSetId ??
    null
  /*
   * The curve stands the face on its edge, so where there is no curve there is nothing to
   * stand it on: the face keeps its own place at the top of the page instead of being pulled
   * up into a box that was never drawn.
   */
  const reservesTrend = trend.length >= 2
  const showsTrend = reservesTrend && historyLoaded

  return (
    <section className="space-y-10">
      {/*
        The hero opens the page with the curve and the exercise's own face standing on it. There
        is no picture behind either: a thumbnail is a small video frame, and one stretched over
        a desktop's width can only ever read as a stretched small video frame, however hard it
        is blurred. What is left is the exercise itself — its face, its name, the span it covers
        and its numbers, each centred down the page's own middle.
      */}
      {/* The exercise's own colours held over the whole hero: the glow reads them, and so
          does the curve, drawn in the exercise's light instead of the theme's accent. */}
      <AmbientScope setIds={heroSetId === null ? [] : [heroSetId]}>
        <div
          ref={heroRef}
          role="group"
          aria-label="Exercise hero"
          className={`ml-[calc(50%-50vw)] w-screen ${HERO_SIZES}`}
        >
          {/*
          The curve is the page's horizon: full bleed, and low enough in its own box that the
          wash beneath it carries on falling behind the face standing on its edge — so the two
          are one thing rather than a graph with a circle after it.

          Nothing until every page of the history is in: a curve is a shape, and one that redrew
          as each page landed would look like the exercise itself changing.
        */}
          {showsTrend ? (
            <TrendChart
              points={trend}
              reading={{
                formatValue: trendFormatter(kind, units, distanceUnits),
                formatPoint: (point) => formatSetNumbers(point.set, units, distanceUnits),
                interactionRef: heroRef,
              }}
              curveBand={TREND_CURVE_BAND}
              className="ambient-tinted fades-on-scroll relative h-[calc(var(--spill)*3)] w-full"
            />
          ) : reservesTrend ? (
            <SkeletonBlock
              label="Loading trend"
              className="trend-skeleton h-[calc(var(--spill)*3)] w-full grayscale"
            >
              <TrendChartSkeleton curveBand={TREND_CURVE_BAND} className="relative h-full w-full" />
            </SkeletonBlock>
          ) : null}

          {/* The hero reads down its own middle, from the face on the curve's edge. */}
          <div
            className={`relative mx-auto flex w-full max-w-7xl flex-col items-center px-4 sm:px-6 ${
              reservesTrend ? 'mt-[calc(var(--spill)*-1)]' : ''
            }`}
          >
            {/* The box the glow centres itself on: snug around the face it is the light of. */}
            <div className="relative">
              <AmbientGlow />
              <HeroFace
                label={`${chosen.latestName} exercise cover`}
                transitionName={pageTransitionName('exercise', chosen.latestName)}
              >
                {heroSetId !== null ? (
                  <SafeImage
                    src={api.mediaUrl(heroSetId, 'thumbnail')}
                    alt=""
                    frameClassName="h-full w-full"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <KindMark
                    records={records}
                    className="h-[calc(var(--face)*0.4)] w-[calc(var(--face)*0.4)] text-ink-muted"
                  />
                )}
              </HeroFace>
            </div>

            {/* The face and the name are one thing — the exercise — so they sit close. */}
            <h1
              className="page-enter mt-3 text-center text-3xl font-medium tracking-tight"
              data-page-enter="0"
              data-testid="page-enter"
            >
              {chosen.latestName}
            </h1>
            {/*
            An exercise logged one way names that way here, where there is no tab to say it —
            the kind is then a fact about the exercise rather than a choice to make.
          */}
            <p
              className="page-enter mt-1 text-center text-sm text-ink-muted"
              data-page-enter="1"
              data-testid="page-enter"
            >
              {methods.length > 1 ? '' : `${KIND_LABELS[kind]} · `}
              {spanOfDays(totals.firstPerformedAt, chosen.lastPerformedAt)}
            </p>

            {/*
            One tab per way the exercise has been logged, most recently logged first, under
            the name it narrows rather than over it. An exercise logged one way shows none —
            which is nearly all of them, so the bar earns its place only where there is a
            choice to make, and a lone tab that answered no press would look like one that did.
          */}
            {methods.length > 1 && (
              <nav
                aria-label="Logging methods"
                className="page-enter scroll-x mt-4 flex max-w-full gap-2 px-0.5 py-0.5"
                data-page-enter="2"
                data-testid="page-enter"
              >
                {methods.map((candidate) => (
                  <Link
                    key={candidate.kind}
                    to="/exercises/$exerciseName"
                    params={{ exerciseName }}
                    // The order and grouping belong to the page, not to one tab, so they come along.
                    search={{
                      method: candidate.kind,
                      ...(orderId !== 'recent' ? { order: orderId } : {}),
                      ...(grouped ? { byWorkout: true } : {}),
                    }}
                    replace
                    resetScroll={false}
                    aria-current={candidate.kind === kind ? 'page' : undefined}
                    className={`flex h-10 shrink-0 items-center gap-1.5 rounded-full px-4 text-sm transition-colors ${
                      candidate.kind === kind
                        ? 'bg-accent/15 text-accent ring-1 ring-accent/40 ring-inset'
                        : 'text-ink-muted ring-1 ring-white/15 ring-inset hover:bg-hover hover:text-ink'
                    }`}
                  >
                    <KindGlyph kind={candidate.kind} className="h-5 w-5" />
                    {KIND_LABELS[candidate.kind]}
                  </Link>
                ))}
              </nav>
            )}

            {/*
            Centred, so they wrap onto a second line rather than sliding off the edge — a row
            that scrolled would have to start at the margin and break the middle.
          */}
            {sets.length > 0 && (
              <div
                className="page-enter mt-4 flex flex-wrap justify-center gap-2"
                data-page-enter="3"
                data-testid="page-enter"
              >
                {heroStats(kind, totals, prs, units, distanceUnits).map((stat) => (
                  <span
                    key={stat.label}
                    className="flex items-center gap-2 rounded-full bg-surface-raised px-3.5 py-2.5 text-sm font-medium"
                  >
                    {stat.icon}
                    {stat.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </AmbientScope>

      {/* The records read as a gallery of the sets that set them, not as a table of numbers. */}
      {(prs.length > 0 || ratioWaiting) && (
        <section
          aria-labelledby="exercise-records"
          className="page-enter space-y-4"
          data-page-enter="4"
          data-testid="page-enter"
        >
          <h2 id="exercise-records" className="px-0.5 text-lg font-medium">
            Records
          </h2>
          {/* Sideways like the workouts under it: records read as a rail, not a wall. */}
          <SidewaysRow role="group" label="Record cards" className="gap-3 pb-1">
            {prs.map((pr) => (
              <RecordCard key={pr.id} pr={pr} units={units} distanceUnits={distanceUnits} />
            ))}
            {ratioWaiting && (
              <SkeletonBlock label="Loading the record against bodyweight" className="shrink-0">
                <div style={{ height: RECORD_CARD.height, width: RECORD_CARD.width }}>
                  <Skeleton className="h-full w-full" />
                </div>
              </SkeletonBlock>
            )}
          </SidewaysRow>
        </section>
      )}

      {sets.length === 0 ? (
        <p className="py-12 text-center text-ink-muted">No sets of this exercise yet.</p>
      ) : (
        <>
          {/* Above the sets, which run on for as long as the exercise has been done. */}
          {theirWorkouts.length > 0 && (
            <section
              aria-labelledby="exercise-workouts"
              className="page-enter space-y-4"
              data-page-enter="5"
              data-testid="page-enter"
            >
              <h2 id="exercise-workouts" className="px-0.5 text-lg font-medium">
                Workouts
              </h2>
              <SidewaysRow className="gap-3 pb-1">
                {theirWorkouts.map((workout) => (
                  <WorkoutThumbnail key={workout.id} workout={workout} sharedTransition={false} />
                ))}
              </SidewaysRow>
            </section>
          )}

          <div className="page-enter" data-page-enter="6" data-testid="page-enter">
            <SetsSection
              sets={sets}
              kind={kind}
              orderId={orderId}
              onOrder={(next) => {
                applySets(next, grouped)
              }}
              grouped={grouped}
              onGrouped={(next) => {
                applySets(orderId, next)
              }}
              workoutName={(workoutId) =>
                [...theirWorkouts, ...state.workouts].find(
                  (candidate) => candidate.id === workoutId,
                )?.name ?? null
              }
            />
          </div>
        </>
      )}
    </section>
  )
}

/** The exercise's stable face and the shape of its page, without any partial history values. */
function ExerciseHistoryLoading({
  name,
  heroSetId,
  api,
}: {
  name: string
  heroSetId: string | null
  api: ReturnType<typeof useApi>
}) {
  return (
    <section className="space-y-10">
      <div className={`ml-[calc(50%-50vw)] w-screen ${HERO_SIZES}`}>
        <SkeletonBlock
          label="Loading trend"
          className="trend-skeleton h-[calc(var(--spill)*3)] w-full grayscale"
        >
          <TrendChartSkeleton curveBand={TREND_CURVE_BAND} className="relative h-full w-full" />
        </SkeletonBlock>
        <div className="relative mx-auto mt-[calc(var(--spill)*-1)] flex w-full max-w-7xl flex-col items-center px-4 sm:px-6">
          <HeroFace
            label={`${name} exercise cover`}
            transitionName={pageTransitionName('exercise', name)}
          >
            {heroSetId !== null ? (
              <SafeImage
                src={api.mediaUrl(heroSetId, 'thumbnail')}
                alt=""
                frameClassName="h-full w-full"
                className="h-full w-full object-cover"
              />
            ) : (
              <LayersIcon className="h-[calc(var(--face)*0.4)] w-[calc(var(--face)*0.4)] text-ink-muted" />
            )}
          </HeroFace>
          <h1 className="mt-3 text-center text-3xl font-medium tracking-tight">{name}</h1>
          <SkeletonBlock
            label="Loading exercise history"
            className="mt-3 flex flex-col items-center gap-4"
          >
            <Skeleton className="h-4 w-52" />
            <div className="flex gap-2" aria-hidden>
              <Skeleton className="h-10 w-24 rounded-full" />
              <Skeleton className="h-10 w-28 rounded-full" />
              <Skeleton className="h-10 w-32 rounded-full" />
            </div>
          </SkeletonBlock>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" aria-hidden>
        <Skeleton className="aspect-square" />
        <Skeleton className="aspect-square" />
        <Skeleton className="aspect-square" />
      </div>
    </section>
  )
}

/**
 * One record as the set that set it. It is a set tile in every way but what is written on it:
 * the same frame, the same picture swelling inside it under the pointer, the same mark and lit
 * ground where nothing was filmed, and the same fade the words are read against — because a
 * record is a set, and a row of them beside a grid of sets that behaved differently would read
 * as a different kind of thing.
 *
 * What it says is its own: the record, its value, the set that produced it and the day it was
 * set, together in the bottom corner.
 */
function RecordCard({
  pr,
  units,
  distanceUnits,
}: {
  pr: ExercisePR
  units: Units
  distanceUnits: DistanceUnits
}) {
  const api = useApi()
  const picture = api.mediaUrl(pr.set.id, 'thumbnail')
  const filmed = pr.set.hasThumbnail
  const tied = pr.sets.length > 1
  /*
   * A stack pages through the sets tied for the record and nothing else, oldest first — the
   * viewer reads its list left to right, so that is what puts the older of them on the left.
   */
  const stacked = [...pr.sets]
    .sort((a, b) => a.performedAt - b.performedAt)
    .map((set) => set.id)
    .join(',')
  const deck = useMemo(
    () => (stacked.includes(',') ? rememberDeck(stacked.split(',')) : undefined),
    [stacked],
  )

  return (
    <div
      style={{ height: RECORD_CARD.height, width: RECORD_CARD.width }}
      className="relative shrink-0"
    >
      {tied && (
        <>
          <span className="absolute inset-0 translate-x-2 translate-y-2 rounded-2xl bg-surface-bright ring-1 ring-white/15" />
          <span className="absolute inset-0 translate-x-1 translate-y-1 rounded-2xl bg-surface-raised ring-1 ring-white/10" />
        </>
      )}
      <Link
        to="/sets/$setId"
        params={{ setId: pr.set.id }}
        search={tied && deck !== undefined ? { deck } : {}}
        className={`${TILE_FRAME} h-full w-full rounded-2xl`}
      >
        <TilePicture
          picture={filmed ? picture : null}
          mark={
            <KindMark
              records={pr.set.records}
              className={`h-15 w-15 text-ink-muted/70 ${TILE_MARK}`}
            />
          }
        />

        {tied && (
          <span className="absolute top-2.5 right-2.5 rounded-full bg-black/55 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm">
            {pr.sets.length} tied sets
          </span>
        )}

        {/*
        Sized by the card the way a tile's text is — 190 across takes the second step of the
        shared scale — and standing on the same fade, where there is footage to stand on.
      */}
        <span
          className={`pointer-events-none absolute inset-x-0 bottom-0 px-3.5 pb-3 ${
            filmed ? `${TILE_SCRIM} pt-15` : 'pt-1'
          }`}
        >
          <span className={`block truncate text-[17px] leading-tight ${tileInkQuiet(filmed)}`}>
            {PR_LABELS[pr.id]}
          </span>
          {/*
          The whole point of the card, so it is plainly the largest thing on it — and plainly
          not a control, which is what the accent would have made of it.
        */}
          <span className={`block truncate text-xl leading-tight font-medium ${tileInk(filmed)}`}>
            {formatPRValue(pr, units, distanceUnits)}
          </span>
          <span className={`block truncate text-[15px] leading-tight ${tileInkQuiet(filmed)}`}>
            {formatSetNumbers(pr.set, units, distanceUnits)}
          </span>
          <span className={`block truncate text-[17px] leading-tight ${tileInkQuiet(filmed)}`}>
            {formatDayLabel(pr.set.performedAt)}
          </span>
        </span>
      </Link>
    </div>
  )
}

/**
 * The exercise's sets, under a control that reorders them — most recent, or the kind's own
 * measures of best — and, while recency keeps the workouts contiguous, a switch that deals
 * the tiles into blocks by workout. Any other order interleaves workouts, so grouping
 * stops applying and cannot be asked for.
 */
function SetsSection({
  sets,
  kind,
  orderId,
  onOrder,
  grouped,
  onGrouped,
  workoutName,
}: {
  sets: readonly LoggedSet[]
  kind: ExerciseKind
  orderId: string
  onOrder: (id: string) => void
  grouped: boolean
  onGrouped: (grouped: boolean) => void
  workoutName: (workoutId: string) => string | null
}) {
  const [shown, setShown] = useState(SETS_SHOWN)
  /*
   * Held still rather than written inline: the foot watches this, and a fresh function on every
   * render would tear the observer down and build it again each time — which is both churn and
   * a second helping of thirty for one arrival at the foot.
   */
  const reachedFoot = useCallback(() => {
    setShown((count) => count + SETS_SHOWN)
  }, [])
  const options = orderOptions(kind)
  const option = options.find((candidate) => candidate.id === orderId) ?? options[0]
  if (!option) {
    return null
  }
  const sorted = [...sets].sort(option.sort)
  const ordered = sorted.slice(0, shown)
  const showBlocks = grouped && option.id === 'recent'

  return (
    <section aria-labelledby="exercise-sets" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h2 id="exercise-sets" className="px-0.5 text-lg font-medium">
          Working sets
        </h2>
        {/* The heading holds the left edge; how to read the sets sits across from it. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <label
            className={`flex items-center gap-2 text-xs ${
              option.id === 'recent' ? 'cursor-pointer text-ink-muted' : 'text-ink-muted/40'
            }`}
          >
            <input
              type="checkbox"
              className="h-5 w-5 accent-accent"
              disabled={option.id !== 'recent'}
              checked={showBlocks}
              onChange={(event) => {
                onGrouped(event.target.checked)
              }}
            />
            Group by workout
          </label>
          <div className="flex rounded-full bg-surface-raised p-1">
            {options.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                aria-pressed={id === option.id}
                onClick={() => {
                  onOrder(id)
                }}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  id === option.id ? 'bg-surface-bright text-ink' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Dealt into blocks or not, what is on the screen is one list, and it is the list a
          set opened from here is leafed through — in the order it is shown in. */}
      <SetDeck sets={ordered}>
        {showBlocks ? (
          <div className="flex flex-col gap-5">
            {workoutBlocks(ordered, workoutName).map((block) => (
              <SetGrid key={block.key} sets={block.sets} columns={6} heading={block.heading} />
            ))}
          </div>
        ) : (
          <SetGrid sets={ordered} columns={6} />
        )}
      </SetDeck>

      {/*
        Thirty at a time, taken as the foot of them comes into view rather than asked for.
        Browsing your own history is not a thing to be granted permission for a page at a
        time — the count exists to keep a page of a thousand sets cheap, not to gate it.
      */}
      {sorted.length > shown && <MoreSets onReach={reachedFoot} />}
    </section>
  )
}
